import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { SiteType } from '@core/enums/tktSite-enum';
import { FilterParser } from '@core/filter-parser';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { ObjectUtils, strictMap } from '@core/utils/object-utils';
import {
  AddSiteGQL,
  ArchiveSiteGQL,
  GetPaginatedSitesGQL,
  GetSiteGQL,
  GetSiteNoGQL,
  GetSitesActiveParentGQL,
  GetSitesByIdsGQL,
  GetSitesByParentGQL,
  GetSitesGQL,
  GetSitesHierarchyByParentIdGQL,
  GetSitesHierarchyGQL,
  GetTicketNoGQL,
  RestoreSiteGQL,
  SiteFilterInput,
  SiteSortInput,
  SortEnumType,
  States,
  UpdateSiteGQL,
  UpdateTicketLocationsGQL,
} from '@generated/queries';
import { BaseService } from '@shared/services/base.service';
import { plainToClass, plainToInstance } from 'class-transformer';
import { BehaviorSubject, firstValueFrom, map, Observable, of } from 'rxjs';
import {CreateSite, Site, UpdateSite } from '../models/tkt-site';
import { UpdateLocation } from '../models/tickets';

@Injectable({
  providedIn: 'root',
})
export class TktSiteService {
  site$: Observable<PageResult<Site>>;
  private siteSubject = new BehaviorSubject<PageResult<Site> | null>(null);
  siteChild$: Observable<PageResult<Site>>;
  facility$: Observable<PageResult<Site>>;
  area$: Observable<PageResult<Site>>;
  areaChild$: Observable<PageResult<Site>>;

  pageFilter: PageFilter = {
    pageIndex: 0,
    activeIndex: 1,
    take: 300,
    search: '',
    state: '',
  };

  vmFilters: SiteFilterInput;
  routeUrl = '';

  constructor(
    private paginatedSites: GetPaginatedSitesGQL,
    private getSitesHierarchy: GetSitesHierarchyGQL,
    private getSite: GetSiteGQL,
    private getSites: GetSitesGQL,
    private getSitesByParent: GetSitesByParentGQL,
     private getTicketNo: GetTicketNoGQL,
    private siteAdd: AddSiteGQL,
    private siteUpdate: UpdateSiteGQL,
    private siteArchive: ArchiveSiteGQL,
    private siteRestore: RestoreSiteGQL,
    private locUpdate:UpdateTicketLocationsGQL,
    private getSiteNo: GetSiteNoGQL,
    private getActiveParentSites: GetSitesActiveParentGQL,
    private localStorageService: LocalStorageDataService,
    private baseService: BaseService,
    private hierarchyById:GetSitesHierarchyByParentIdGQL,
    private getSiteByIds: GetSitesByIdsGQL

  ) {
    this.routeUrl = this.baseService.getBaseUrl();
  }


  clearAreaSite(){
    this.siteChild$ = of(new PageResult<Site>());
    this.areaChild$ = of(new PageResult<Site>());
  }

  clearArea(){
    this.areaChild$ = of(new PageResult<Site>());
  }

  checkAreaValues(){
    this.areaChild$.forEach((res) => {
      console.log('Area Child:',res.data);
    })
  }

  getPaginatedSites(
    filter: PageFilter,
    siteType: SiteType,
    parentId: string = null,
    policy?: string
  ): Promise<PageResult<Site>> {
    const vmFilters: SiteFilterInput = {
      or: [
        {
          siteName: {
            contains: filter.search.trim(),
          },
        },
        {
          siteNo: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          parentId: {
            eq: parentId,
          },
        },
        {
          type: {
            eq: siteType,
          },
        },
      ],
    };
    filter.parentId = null;
    const filters = FilterParser.parseFilter({
      state: filter.state,
      //parentId: filter.parentId,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      vmFilters.and.push(filters);
    }
    FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.paginatedSites
        .fetch(
          {
            filter: vmFilters,
            skip: filter.skip,
            take: filter.take,
          },
          {
            fetchPolicy: policy
              ? (policy as FetchPolicy)
              : this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map(
            (x) => // eslint-disable-next-line
              new PageResult<Site>(x.data.supportTicket.ticketsSites as any)
          )
        )
    );
  }

  getSitesByIds(siteIds: string[], siteType: number): Promise<Site[]> {
    return firstValueFrom(
      this.getSiteByIds
        .fetch({ siteIds , siteType: siteType}, { fetchPolicy: 'network-only' })
        .pipe(
          map((x) =>
            plainToInstance(
              Site,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              x.data.supportTicket.ticketsSiteByIds as any[]
            )
          )
        )
    );
  }

  getPaginatedSitesHierarchy(
    filter: PageFilter,
    siteType: SiteType,
    policy?: string
  ): Promise<Site[]> {
    const vmFilters: SiteFilterInput = {
      or: [
        {
          siteName: {
            contains: filter.search.trim(),
          },
        },
        {
          siteNo: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          // parentId: {
          //   eq: parentId,
          // },
        },
        {
          type: {
            eq: siteType,
          },
        },
      ],
    };

    filter.parentId = null;

    const filters = FilterParser.parseFilter({
      state: filter.state,
    });

    if (!ObjectUtils.isObjEmpty(filters)) {
      vmFilters.and.push(filters);
    }

    FilterParser.parsePageFilter(filter);

    return firstValueFrom(
      this.getSitesHierarchy
        .fetch(
          {
            search : filter.search,
          },
          {
            fetchPolicy: policy
              ? (policy as FetchPolicy)
              : this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(Site, x.data.supportTicket.ticketsSitesHierarchy)
          )
        )
    );
  }


  getPaginatedSitesHierarchyByParentId(parentId: string, fetchPolicy?: FetchPolicy): Promise<Site[]> {
    return firstValueFrom(
      this.hierarchyById
        .fetch(
          {
            facilityId: parentId
          },
          {
            fetchPolicy:
              fetchPolicy ??
              this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(Site, x.data.supportTicket.ticketsSitesHierarchy)
          )
        )
    );
  }

  getPaginatedActiveSites(
    filter: PageFilter,
    type: number,
    parentId: string = null,
    policy?: string
  ): Promise<PageResult<Site>> {
    const vmFilters: SiteFilterInput = {
      or: [
        {
          siteName: {
            contains: filter.search.trim(),
          },
        },
        {
          siteNo: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          parentId: {
            eq: parentId,
          },
        },
        {
          state: {
            eq: States.Active,
          },
        },
        {
          type: {
            eq: type,
          },
        },
      ],
    };
    filter.parentId = null;
    const filters = FilterParser.parseFilter({
      state: filter.state,
      //parentId: filter.parentId,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      vmFilters.and.push(filters);
    }
    FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.paginatedSites
        .fetch(
          {
            filter: vmFilters,
            skip: filter.skip,
            take: filter.take,
          },
          {
            fetchPolicy: policy
              ? (policy as FetchPolicy)
              : this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map(
            (x) =>
                 // eslint-disable-next-line
              new PageResult<Site>(x.data.supportTicket.ticketsSites as any)
          )
        )
    );
  }

  getsite(id: string, fetchPolicy?: FetchPolicy): Promise<Site> {
    return firstValueFrom(
      this.getSite
        .fetch(
          {
            id,
          },
          {
            fetchPolicy:
              fetchPolicy ??
              this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map((x) =>
            plainToClass(Site, x.data.supportTicket.ticketsSites.items[0])
          )
        )
    );
  }



  getTicketsNo(): Promise<string> {
  return firstValueFrom(
    this.getTicketNo
      .fetch(
        {},
        {
          fetchPolicy: 'network-only',
        }
      )
      .pipe(
        map((x) => x.data.supportTicket.ticketNo)
      )
  );
}

addSite(model: Site): Promise<string> {
  try {
    const mappedModel = strictMap(CreateSite, model);
    return firstValueFrom(
      this.siteAdd
        .mutate({ model: mappedModel })
        .pipe(map((x) => {
          if (!x?.data?.supportTicket?.site?.add) {
            throw new Error('Mutation response is incomplete');
          }
          return x.data.supportTicket.site.add;
        }))
    );
  } catch (err) {
    return Promise.reject(err);
  }
}


  updateSite(model: Site): Promise<string> {
    return firstValueFrom(
      this.siteUpdate
        .mutate({ model: strictMap(UpdateSite, model) })
        .pipe(map((x) => x.data.supportTicket.site.update))
    );
  }


  updateTicketLocation(model:UpdateLocation):Promise<string>{
    return firstValueFrom(
      this.locUpdate
        .mutate({model: strictMap(UpdateLocation,model)})
        .pipe(map((x) => x.data.supportTicket.tickets.updateLocation))
    )
  }

  archiveSite(id: string): Promise<string> {
    return firstValueFrom(
      this.siteArchive
        .mutate({ siteId: id })
        .pipe(map((x) => x.data.supportTicket.site.archive))
    );
  }

  restoreSite(id: string): Promise<string> {
    return firstValueFrom(
      this.siteRestore
        .mutate({ siteId: id })
        .pipe(map((x) => x.data.supportTicket.site.restore))
    );
  }

  fetchSite(
    event: boolean,
    type: number,
    fetchPolicy?: string,
    orderBy?: SiteSortInput,
    page?:PageFilter
  ): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
     // eslint-disable-next-line
    const order: SiteSortInput[] = orderBy
      ? [orderBy]
      : [
          {
            createdOn: SortEnumType.Desc,
          },
        ];


    const result = this.getSites
      .fetch(
      {
        search: this.pageFilter.search,
        skip: page?.skip != null ? page.skip : this.pageFilter.skip,
        take: page?.take != null ? page.take : this.pageFilter.take,
        type: type,
        order: null,
      },
      {
        fetchPolicy: fetchPolicy
        ? (fetchPolicy as FetchPolicy)
        : this.localStorageService.getFetchPolicy('tktSites'),
      }
      )
      .pipe(
      map(
           // eslint-disable-next-line
        (x) => new PageResult<Site>(x.data.supportTicket.SortedSites as any)
      )
      );
    if (type == SiteType.LOCATION) {
      this.site$ = result;
    } else if (type == SiteType.FACILITY) {
      this.facility$ = result;
    } else if (type == SiteType.AREA) {
      this.area$ = result;
    }
  }

  fetchSiteByParent(
    event: boolean,
    type: number,
    fetchPolicy?: string,
    parentId?: string[],
    //eslint-disable-next-line
    orderBy?: SiteSortInput
  ): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }



    const result = this.getSitesByParent
      .fetch(
        {
          search: this.pageFilter.search,
          skip: this.pageFilter.skip,
          take: 300,
          type: type,
          parentIds: parentId,
        },
        {
          fetchPolicy: fetchPolicy
            ? (fetchPolicy as FetchPolicy)
            : this.localStorageService.getFetchPolicy('tktSites'),
        }
      )
      .pipe(
        map(
             // eslint-disable-next-line
          (x) => new PageResult<Site>(x.data.supportTicket.SortedSites as any)
        )
      );

    if (type == SiteType.LOCATION) {
      this.siteChild$ = result;
    } else if (type == SiteType.FACILITY) {
      this.facility$ = result;
    } else if (type == SiteType.AREA) {
      this.areaChild$ = result;
    }
  }

  getSitesActive(type: number, fetchPolicy?: FetchPolicy): Promise<Site[]> {
    return firstValueFrom(
      this.getActiveParentSites
        .fetch(
          {
            type,
          },
          {
            fetchPolicy:
              fetchPolicy ??
              this.localStorageService.getFetchPolicy('tktSites'),
          }
        )
        .pipe(
          map((x) => {
            // Map the fetched data and filter childSite based on state = ACTIVE
            return plainToInstance(
              Site,
              x.data.supportTicket.sitesArray.map((site) => ({
                ...site,
                childSite: site.childSite.filter(
                  (child) => child.state === 'ACTIVE'
                ),
              }))
            );
          })
        )
    );
  }

  setSite(siteId: string, type?: number) {
    const result = this.getSite
      .fetch(
        {
          id: siteId,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('tktSites'),
        }
      )
      .pipe(
        map(
             // eslint-disable-next-line
          (x) => new PageResult<Site>(x.data.supportTicket.ticketsSites as any)
        )
      );
    if (type == SiteType.LOCATION) {
      this.site$ = result;
    } else if (type == SiteType.FACILITY) {
      this.facility$ = result;
    } else if (type == SiteType.AREA) {
      this.area$ = result;
    }
    return this.site$;
  }

  getSiteDetail(siteId: string) {
    return this.getSite
      .fetch(
        {
          id: siteId,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('tktSites'),
        }
      )
      .pipe(
        map(
           // eslint-disable-next-line
          (x) => new PageResult<Site>(x.data.supportTicket.ticketsSites as any)
        )
      );
  }

  getSitesNo(): Promise<string> {
    return firstValueFrom(
      this.getSiteNo
        .fetch(
          {},
          {
            fetchPolicy: 'network-only',
          }
        )
        .pipe(map((x) => x.data.supportTicket.siteNo))
    );
  }

  fetchNextSites(event: number, type: number, orderBy?: SiteSortInput): void {
    this.pageFilter.skip = event;
    this.fetchSite(false, type, null, orderBy);
  }

  searchInSites(event, type: number, orderBy?: SiteSortInput): void {
    this.pageFilter.search = event;
    this.pageFilter.skip = null;
    this.fetchSite(false, type, null, orderBy);
  }

  fetchNextSitesByParent(
    event: number,
    type: number,
    orderBy?: SiteSortInput,
    parentIds?: string[]
  ): void {
    this.pageFilter.skip = event;
    this.fetchSiteByParent(false, type, null, parentIds, orderBy);
  }

  searchInSitesByParent(
    event,
    type: number,
    orderBy?: SiteSortInput,
    parentIds?: string[]
  ): void {
    this.pageFilter.search = event;
    this.pageFilter.skip = null;
    this.fetchSiteByParent(false, type, null, parentIds, orderBy);
  }

  clearObservables(){
    this.site$ = this.siteSubject.asObservable();
    this.siteChild$ = this.siteSubject.asObservable();
    //
    this.area$ = this.siteSubject.asObservable();
    this.areaChild$ = this.siteSubject.asObservable();
    //
    this.siteChild$ = new  Observable<PageResult<Site>>;
    this.site$ = new  Observable<PageResult<Site>>;
    this.siteSubject.next(null);
  }
}
